import React from 'react';
import { motion } from 'framer-motion';
import { CTAButton } from './CTAButton';
import { TrendingUpIcon, ZapIcon, ShieldCheckIcon } from 'lucide-react';
interface SolutionSectionProps {
  onBookCall: () => void;
}
export function SolutionSection({
  onBookCall
}: SolutionSectionProps) {
  return <section className="bg-navy py-20 md:py-28 relative overflow-hidden" data-id="element-138">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10" data-id="element-139">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-accent rounded-full blur-3xl" data-id="element-140" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-gold rounded-full blur-3xl" data-id="element-141" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-142">
        {/* Solution Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-143">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6" data-id="element-144">
            Introducing{' '}
            <span className="text-gold" data-id="element-145">
              The Automatic Paralegal Assistant™
            </span>
          </h2>
          <p className="text-lg md:text-xl text-white/80 max-w-3xl mx-auto" data-id="element-146">
            The old way was hiring more people. The new way is installing a
            system. We provide your firm with a custom trained, agentic AI
            paralegal that works 24/7 to automate your most time consuming tasks
            and drive profitability.
          </p>
        </motion.div>

        {/* Key Benefits */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20" data-id="element-147">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center" data-id="element-148">
            <div className="w-14 h-14 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-149">
              <ZapIcon className="w-7 h-7 text-gold" data-id="element-150" />
            </div>
            <h3 className="font-heading text-xl font-bold text-white mb-2" data-id="element-151">
              10x More Efficient
            </h3>
            <p className="text-white/60 text-sm" data-id="element-152">
              Than traditional paralegal workflows
            </p>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center" data-id="element-153">
            <div className="w-14 h-14 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-154">
              <TrendingUpIcon className="w-7 h-7 text-gold" data-id="element-155" />
            </div>
            <h3 className="font-heading text-xl font-bold text-white mb-2" data-id="element-156">
              70% Cost Reduction
            </h3>
            <p className="text-white/60 text-sm" data-id="element-157">
              Compared to hiring additional staff
            </p>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 text-center" data-id="element-158">
            <div className="w-14 h-14 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-159">
              <ShieldCheckIcon className="w-7 h-7 text-gold" data-id="element-160" />
            </div>
            <h3 className="font-heading text-xl font-bold text-white mb-2" data-id="element-161">
              24/7 Operation
            </h3>
            <p className="text-white/60 text-sm" data-id="element-162">
              Never misses a deadline or lead
            </p>
          </div>
        </motion.div>

        {/* Why Now Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="bg-navy-light border border-gold/30 rounded-2xl p-8 md:p-12 mb-12" data-id="element-163">
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-gold mb-6 text-center" data-id="element-164">
            Why This is a Once in a Generation Opportunity
          </h3>
          <p className="text-white/80 text-lg md:text-xl leading-relaxed text-center max-w-4xl mx-auto" data-id="element-165">
            For the first time ever, tools like ClawBot & Manus allow you to
            gain an almost{' '}
            <span className="text-white font-semibold" data-id="element-166">unfair advantage</span>.
            While your competitors are still hiring expensive overseas
            paralegals and drowning in paperwork, you can leapfrog them by
            installing an AI workforce that is 10x more efficient and 70%
            cheaper.{' '}
            <span className="text-gold font-semibold" data-id="element-167">
              This window of opportunity will not last forever.
            </span>{' '}
            Early adopters will build insurmountable leads.
          </p>
        </motion.div>

        {/* CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="text-center" data-id="element-168">
          <CTAButton onClick={onBookCall} data-id="element-169">Start Your Application</CTAButton>
        </motion.div>
      </div>
    </section>;
}